import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  createAllowedSignupEmail,
  queryAllowedSignupEmails,
} from '@/features/superuser/data/allowedSignupEmails';
import { useNotifications } from '@/providers/notificationHooks';
import { queryKeys, useInvalidateQueries } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { Inline, Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { formatDate } from '@/ui/formatters/formatDate';
import { type GridColDef } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { AllowedSignupEmailDto } from '@flowqa/shared';

export const AllowedSignupEmailsTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const [addOpen, setAddOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleCloseAdd = useCallback(() => {
    setAddOpen(false);
    setEmailValue('');
    setErrorMessage(null);
  }, []);

  const createMutation = useMutation({
    mutationFn: async (email: string) => createAllowedSignupEmail({ email }, accessToken),
    onSuccess: async (created) => {
      notify({ message: `Allowed ${created.email} to sign up`, tone: 'green' });
      await invalidateQueries([{ queryKey: queryKeys.superuserAllowedSignups() }]);
      handleCloseAdd();
    },
    onError: (error) => {
      setErrorMessage(String(error));
    },
  });

  const columns = useMemo<Array<GridColDef<AllowedSignupEmailDto>>>(() => {
    const emailColumn: GridColDef<AllowedSignupEmailDto> = {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      valueGetter: (_value, row) => row.email,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.email}
        </Text>
      ),
    };
    const createdAtColumn: GridColDef<AllowedSignupEmailDto> = {
      field: 'createdAt',
      headerName: 'Added',
      flex: 1,
      type: 'date',
      valueGetter: (_value, row) => {
        const raw = row.createdAt;
        if (raw instanceof Date) return raw;
        if (typeof raw === 'string') {
          const parsed = new Date(raw);
          return Number.isNaN(parsed.valueOf()) ? null : parsed;
        }
        return null;
      },
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatDate(params.row.createdAt) || '-'}
        </Text>
      ),
    };
    return [emailColumn, createdAtColumn];
  }, []);

  const queryFn = async (
    input: GridQueryInput,
  ): Promise<{ items: AllowedSignupEmailDto[]; total: number; page: number; pageSize: number }> => {
    return await queryAllowedSignupEmails(input, accessToken);
  };

  const trimmedEmail = emailValue.trim();
  const disableAdd = trimmedEmail.length === 0;
  const addEmailField = (
    <TextField
      label="Email"
      value={emailValue}
      onChange={(event) => {
        setEmailValue(event.target.value);
        setErrorMessage(null);
      }}
      error={Boolean(errorMessage)}
      helperText={errorMessage ?? ' '}
      autoFocus
      fullWidth
    />
  );

  return (
    <>
      <Inline justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text role="sectionTitle">Allowed Signups</Text>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            setAddOpen(true);
            setErrorMessage(null);
          }}
        >
          Add Email
        </Button>
      </Inline>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<AllowedSignupEmailDto>
          queryPath="superuser-allowed-signups"
          queryKeyPrefix={queryKeys.superuserAllowedSignups()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          autoHeight
          noRowsText="No allowed signups"
          disableColumnMenu
        />
      </Surface>
      <ConfirmDialog
        open={addOpen}
        title="Add Allowed Signup"
        confirmLabel="Add"
        confirmColor="primary"
        confirmDisabled={disableAdd}
        loading={createMutation.isPending}
        onClose={handleCloseAdd}
        onConfirm={() => {
          if (disableAdd) return;
          void createMutation.mutateAsync(trimmedEmail);
        }}
        description={addEmailField}
        maxWidth="xs"
      />
    </>
  );
};
